import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import './pages.css';
import { safeDate, formatDate as safeFmtDate } from '../utils/dateUtils';

const METHOD_ICON = { UPI: '📱', 'Net Banking': '🏦', 'Debit Card': '💳', 'Credit Card': '💳' };

const formatDate = (date) => {
    if (!date) return '—';
    const d = date instanceof Date ? date : safeDate(date);
    if (!d) return '—';
    return d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
};

const getTenureDueDate = (loan) => {
    const tenureDays = Number(loan.tenure_months || 0);
    const isAwaitingDisbursal = !['Approved', 'Active'].includes(loan.status) &&
        !['Admin_Approved', 'Disbursed'].includes(loan.approval_status);
    const baseDateValue = isAwaitingDisbursal ? null : (loan.disbursement_date || loan.reviewed_at || loan.created_at);
    const baseDate = baseDateValue ? safeDate(baseDateValue) : new Date();
    const savedDueDate = loan.due_date ? safeDate(loan.due_date) : null;
    const hasSavedDueDate = savedDueDate && !Number.isNaN(savedDueDate.getTime());

    if (!Number.isFinite(tenureDays) || tenureDays <= 0 || !baseDate || Number.isNaN(baseDate.getTime())) {
        return hasSavedDueDate ? savedDueDate : null;
    }

    const dueDate = new Date(baseDate);
    dueDate.setDate(dueDate.getDate() + tenureDays);
    return hasSavedDueDate && savedDueDate > dueDate ? savedDueDate : dueDate;
};

function PaymentsPage() {
    const navigate = useNavigate();
    const [paymentHistory, setPaymentHistory] = useState([]);
    const [upcomingPayments, setUpcomingPayments] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [activeTab, setActiveTab] = useState('upcoming');
    const API_URL = process.env.REACT_APP_API_URL || '';

    useEffect(() => {
        const session = JSON.parse(localStorage.getItem('userSession'));
        if (!session?.userId) return setIsLoading(false);

        Promise.all([
            fetch(`${API_URL}/api/payments/${session.userId}`).then(r => r.json()),
            fetch(`${API_URL}/api/loans/${session.userId}`).then(r => r.json()),
        ]).then(([pData, lData]) => {
            if (pData.success) {
                setPaymentHistory(pData.payments.map(p => ({
                    id: p.id,
                    date: safeFmtDate(p.payment_date),
                    loanType: p.description || 'EMI Payment',
                    accountNumber: p.loan_id ? `LN00${p.loan_id}` : 'N/A',
                    amount: Number(p.amount).toLocaleString('en-IN'),
                    rawAmount: Number(p.amount),
                    status: p.status === 'Success' ? 'Paid' : p.status,
                    method: p.payment_method || 'Online',
                })));
            }
            if (lData.success) {
                const today = new Date();
                setUpcomingPayments(lData.loans.filter(l => l.status === 'Active').map(l => {
                    const due = getTenureDueDate(l) || new Date(l.due_date);
                    const days = Math.ceil((due - today) / 86400000);
                    return {
                        id: l.id,
                        loanType: l.type,
                        accountNumber: l.account_number,
                        amount: Number(l.emi).toLocaleString('en-IN'),
                        rawAmount: Number(l.emi),
                        dueDate: formatDate(due),
                        daysLeft: days,
                        urgency: days <= 3 ? 'danger' : days <= 7 ? 'warn' : 'ok',
                    };
                }));
            }
        }).catch(() => {}).finally(() => setIsLoading(false));
    }, [API_URL]);

    const totalPaid = paymentHistory.reduce((s, p) => s + p.rawAmount, 0);
    const totalDue  = upcomingPayments.reduce((s, p) => s + p.rawAmount, 0);

    if (isLoading) return <div className="pg-loading"><div className="pg-spinner" /><span>Loading payments…</span></div>;

    return (
        <div className="pg-root">
            {/* Header */}
            <div className="pg-header">
                <div className="pg-header-text">
                    <h1>💳 Payments</h1>
                    <p>Track your upcoming EMIs and payment history</p>
                </div>
                <div className="pg-header-actions">
                    <button className="pg-btn pg-btn-primary" onClick={() => navigate('/dashboard/loans')}>
                        View Loans →
                    </button>
                </div>
            </div>

            {/* Stats */}
            <div className="pg-stat-strip">
                <div className="pg-stat">
                    <span className="pg-stat-icon">⏰</span>
                    <span className="pg-stat-label">Upcoming Due</span>
                    <span className="pg-stat-value">{upcomingPayments.length}</span>
                </div>
                <div className="pg-stat">
                    <span className="pg-stat-icon">💸</span>
                    <span className="pg-stat-label">Total Due</span>
                    <span className="pg-stat-value" style={{ fontSize: '1.05rem' }}>₹{totalDue.toLocaleString('en-IN')}</span>
                </div>
                <div className="pg-stat">
                    <span className="pg-stat-icon">✅</span>
                    <span className="pg-stat-label">Payments Made</span>
                    <span className="pg-stat-value">{paymentHistory.length}</span>
                </div>
                <div className="pg-stat">
                    <span className="pg-stat-icon">💰</span>
                    <span className="pg-stat-label">Total Paid</span>
                    <span className="pg-stat-value" style={{ fontSize: '1.05rem' }}>₹{totalPaid.toLocaleString('en-IN')}</span>
                </div>
            </div>

            {/* Tabs */}
            <div style={{ display: 'flex', gap: '0.5rem', borderBottom: '1px solid #cfe4fa', paddingBottom: '0' }}>
                {[['upcoming', '⏰ Upcoming'], ['history', '📋 History']].map(([key, label]) => (
                    <button key={key} onClick={() => setActiveTab(key)} style={{
                        padding: '0.65rem 1.25rem',
                        borderRadius: '10px 10px 0 0',
                        border: '1px solid',
                        borderBottom: activeTab === key ? '1px solid white' : '1px solid #cfe4fa',
                        fontWeight: '700',
                        fontSize: '0.84rem',
                        cursor: 'pointer',
                        background: activeTab === key ? 'white' : '#f4f9ff',
                        color: activeTab === key ? '#0755d9' : '#64748b',
                        borderColor: '#cfe4fa',
                        marginBottom: activeTab === key ? '-1px' : '0',
                        transition: 'all 0.15s',
                    }}>
                        {label}
                    </button>
                ))}
            </div>

            {activeTab === 'upcoming' && (
                <div className="pg-panel">
                    <div className="pg-panel-header">
                        <h2>Upcoming EMI Payments</h2>
                        <span className="pg-panel-badge">{upcomingPayments.length} due</span>
                    </div>
                    {upcomingPayments.length === 0 ? (
                        <div className="pg-empty">
                            <span className="pg-empty-icon">🎉</span>
                            <h3>All caught up!</h3>
                            <p>No upcoming payments at this time.</p>
                        </div>
                    ) : (
                        <div className="pg-list">
                            {upcomingPayments.map(p => (
                                <div key={p.id} className="pg-list-item" style={{
                                    borderLeft: `4px solid ${p.urgency === 'danger' ? '#ef4444' : p.urgency === 'warn' ? '#f59e0b' : '#10b981'}`
                                }}>
                                    <div className="pg-list-item-icon" style={{
                                        background: p.urgency === 'danger' ? '#fee2e2' : p.urgency === 'warn' ? '#fef3c7' : '#dcfce7'
                                    }}>
                                        💳
                                    </div>
                                    <div className="pg-list-item-body">
                                        <span className="pg-list-item-title">{p.loanType}</span>
                                        <span className="pg-list-item-sub">{p.accountNumber} · Due {p.dueDate}</span>
                                    </div>
                                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '0.4rem' }}>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                            <span style={{ fontSize: '1.1rem', fontWeight: '800', color: '#1e1b4b' }}>₹{p.amount}</span>
                                            <span className={`pg-badge ${p.urgency === 'danger' ? 'pg-badge-red' : p.urgency === 'warn' ? 'pg-badge-amber' : 'pg-badge-green'}`}>
                                                {p.daysLeft <= 0 ? 'Overdue' : `${p.daysLeft}d left`}
                                            </span>
                                        </div>
                                        <button 
                                            className="pg-btn pg-btn-primary" 
                                            style={{ padding: '0.4rem 0.8rem', fontSize: '0.8rem', borderRadius: '6px' }}
                                            onClick={() => navigate('/dashboard/make-payment', { state: { loanId: p.id } })}
                                        >
                                            Pay Now
                                        </button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            )}

            {activeTab === 'history' && (
                <div className="pg-panel">
                    <div className="pg-panel-header">
                        <h2>Payment History</h2>
                        <span className="pg-panel-badge">{paymentHistory.length} transactions</span>
                    </div>
                    {paymentHistory.length === 0 ? (
                        <div className="pg-empty">
                            <span className="pg-empty-icon">📋</span>
                            <h3>No transactions yet</h3>
                            <p>Your payment history will appear here once you start paying EMIs.</p>
                        </div>
                    ) : (
                        <>
                            <div className="pg-table-wrap">
                                <table className="pg-table">
                                    <thead>
                                        <tr>
                                            <th>Date</th>
                                            <th>Loan</th>
                                            <th>Account</th>
                                            <th>Method</th>
                                            <th>Amount</th>
                                            <th>Status</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {paymentHistory.map(p => (
                                            <tr key={p.id}>
                                                <td>{p.date}</td>
                                                <td>{p.loanType}</td>
                                                <td style={{ fontFamily: 'monospace', fontSize: '0.78rem' }}>{p.accountNumber}</td>
                                                <td>
                                                    <span style={{ display: 'flex', alignItems: 'center', gap: '0.35rem' }}>
                                                        {METHOD_ICON[p.method] || '💰'} {p.method}
                                                    </span>
                                                </td>
                                                <td className="pg-table-amount">₹{p.amount}</td>
                                                <td><span className="pg-badge pg-badge-green">✓ {p.status}</span></td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                            <div style={{
                                display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                                marginTop: '1rem', padding: '0.85rem 1rem',
                                background: '#f4f9ff', borderRadius: '12px', border: '1px solid #dcecff',
                            }}>
                                <span style={{ fontSize: '0.82rem', fontWeight: '600', color: '#64748b' }}>Total Amount Paid</span>
                                <span style={{ fontSize: '1.1rem', fontWeight: '800', color: '#0755d9' }}>₹{totalPaid.toLocaleString('en-IN')}</span>
                            </div>
                        </>
                    )}
                </div>
            )}
        </div>
    );
}

export default PaymentsPage;
